import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';

import { Client } from 'src/app/shared/client';
import { Person } from 'src/app/shared/person';
import { ClientApiService } from 'src/app/services/client-api.service';
import { AgentExtractedIdentitySignals } from 'src/app/shared/agent-data';
import { deriveShortNameCandidate, phoneDigits, splitContactName } from 'src/app/shared/agent-contact-utils';

export interface AgentQuickCreateResult {
  cliId: number | null;
  persId: number | null;
}

type QuickCreateState = 'form' | 'saving' | 'contact-save-failed' | 'existing-match';

/**
 * AG-21b -- creates a new `Clients` row (+ optional `Person` contact) or adds
 * a new `Person` contact to an already-selected company, pre-filled from the
 * agent's Step 2 email-signature extraction (`AgentExtractedIdentitySignals`,
 * AG-21a). Never writes without an explicit manager click -- "offer a
 * reviewable action, never auto-write" per
 * `webyto-agent/docs/CUSTOMER-RESOLUTION-DESIGN.md`. See
 * docs/PROMPT_agent-customer-creation.md for the full design.
 *
 * Two-call sequencing, not atomic -- see that prompt's own "Two-call
 * sequencing" section for why a transactional backend route was
 * deliberately not built for this. In 'new-company' mode, a Person-save
 * failure after a successful Client-save does NOT retry the Client POST
 * (would create a duplicate company) -- the newly-created CLIID stays
 * visible and only the contact half is retried.
 */
@Component({
  selector: 'app-agent-customer-quick-create',
  templateUrl: './agent-customer-quick-create.component.html',
  styleUrls: ['./agent-customer-quick-create.component.scss'],
})
export class AgentCustomerQuickCreateComponent implements OnChanges {
  @Input() mode: 'new-company' | 'new-contact' = 'new-company';
  @Input() extractedSignals: AgentExtractedIdentitySignals | null = null;
  /** Envelope sender address (`AgentRequestDetail.sender_email`) -- always known,
   *  no extraction needed, used as the contact's default email. */
  @Input() senderEmail: string | null = null;
  /** Only read in 'new-contact' mode -- the already-selected company's `CLIID`. */
  @Input() cliId: number | null = null;
  /** Organization address, split into `Clients`' own field shape
   *  (`AgentPasteToCreateComponent`'s `getAddressPostalCode()`/`getAddressCountry()`/
   *  `getAddressCity()`/`getAddressStreet()`), when the pasted text stated one --
   *  `CLIENTZIP`/`CLIENTCOUNTRY`/`CLIENTCITY`/`CLIENTADDR1`, never `Person`.
   *  `addressStreet` alone (the rest null) means the extractor couldn't
   *  confidently split the address -- the whole text still lands in
   *  `CLIENTADDR1`, same fallback `register-data-parsing.ts`'s
   *  `parseRegisterAddress()` already uses for the company-registry lookup.
   *  Only meaningful in 'new-company' mode (see the form template); in
   *  'new-contact' mode there is no company row being created here to attach
   *  it to, so these are accepted but not shown or written. */
  @Input() addressPostalCode: string | null = null;
  @Input() addressCountry: string | null = null;
  @Input() addressCity: string | null = null;
  @Input() addressStreet: string | null = null;

  /** Emitted after a successful save -- host decides what to do with it (pre-select
   *  through companyselector/personselector, never written directly into
   *  newDocument.DOCCLIENTSID/DOCPERSONID -- same "never silently trusted at
   *  default" rule every other resolution path in this panel already follows). */
  @Output() created = new EventEmitter<AgentQuickCreateResult>();

  state: QuickCreateState = 'form';
  existingContacts: Person[] = [];
  matchedExisting: Person | null = null;

  /** Pre-filled with a regex-derived candidate (`deriveShortNameCandidate()`)
   *  when the extracted full name matches a "PREFIX + quoted name" shape
   *  (e.g. `ТОВ "АБР Електрик"` -> `АБР Електрик`); left empty otherwise, in
   *  which case `buildNewClient()` falls back to the full company name.
   *  Always editable -- shown as its own field so the manager can correct a
   *  wrong or missing derivation before saving. */
  companyNickname = '';
  companyName = '';
  registrationCode = '';
  zip = '';
  /** Defaults to 'Україна' when the extractor found no explicit country --
   *  this business's clients are overwhelmingly domestic; still editable
   *  before saving. */
  country = '';
  city = '';
  street = '';
  firstName: string | null = null;
  lastName: string | null = null;
  phone: string | null = null;
  email: string | null = null;

  /** Set once 'new-company' mode's company POST succeeds, even if the
   *  contact POST that follows then fails -- lets retryContact() re-attempt
   *  just the contact half without re-creating the company. */
  createdCliId: number | null = null;

  constructor(
    private cliApi: ClientApiService,
    private messageService: MessageService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['extractedSignals'] || changes['mode'] || changes['senderEmail'] ||
      changes['addressPostalCode'] || changes['addressCountry'] || changes['addressCity'] || changes['addressStreet']
    ) {
      this.prefillFromSignals();
    }
    if (changes['cliId'] && this.mode === 'new-contact' && this.cliId) {
      this.loadExistingContacts();
    }
  }

  get hasUsefulSignal(): boolean {
    return !!(this.extractedSignals?.companyName || this.extractedSignals?.contactFullName);
  }

  private prefillFromSignals(): void {
    const s = this.extractedSignals;
    this.companyNickname = deriveShortNameCandidate(s?.companyName ?? null) ?? '';
    this.companyName = s?.companyName ?? '';
    this.registrationCode = s?.registrationCode ?? '';
    this.zip = this.addressPostalCode ?? '';
    this.country = this.addressCountry ?? 'Україна';
    this.city = this.addressCity ?? '';
    this.street = this.addressStreet ?? '';
    const { fname, lname } = splitContactName(s);
    this.firstName = fname;
    this.lastName = lname;
    this.phone = s?.phone ?? null;
    this.email = this.senderEmail ?? null;
    this.createdCliId = null;
    this.state = 'form';
  }

  private loadExistingContacts(): void {
    if (!this.cliId) { return; }
    this.cliApi.getPersonsByClientId(this.cliId).subscribe({
      next: (persons) => {
        this.existingContacts = persons;
        this.matchedExisting = this.findDuplicate(persons);
        this.state = this.matchedExisting ? 'existing-match' : 'form';
      },
      error: () => { this.existingContacts = []; this.matchedExisting = null; },
    });
  }

  /** Dedupe check per the design doc: "if the extracted phone/email already
   *  matches an existing Person row, show that instead of offering to
   *  create a duplicate." Compares on the same last-9-digit phone key
   *  webyto-agent's own `normalizePhoneDigits()` uses, so a differently
   *  formatted but identical number still matches. */
  private findDuplicate(persons: Person[]): Person | null {
    const wantPhone = phoneDigits(this.phone);
    const wantEmail = (this.email ?? '').trim().toLowerCase();
    return persons.find(p =>
      (!!wantEmail && (p.PERSONEMAIL ?? '').trim().toLowerCase() === wantEmail) ||
      (wantPhone.length === 9 && [p.PERSONPHONE, p.PERSONCPHONE, p.PERSONPHONE1].some(v => phoneDigits(v) === wantPhone))
    ) ?? null;
  }

  private hasContactData(): boolean {
    return !!(this.firstName || this.lastName || this.phone || this.email);
  }

  onCreate(): void {
    if (this.mode === 'new-company') {
      this.createCompanyAndContact();
    } else {
      this.createContactOnly();
    }
  }

  useExistingContact(): void {
    if (!this.matchedExisting) { return; }
    this.created.emit({ cliId: null, persId: this.matchedExisting.PERSID });
  }

  retryContact(): void {
    if (!this.createdCliId) { return; }
    this.saveContact(this.createdCliId, true);
  }

  private createCompanyAndContact(): void {
    if (!this.companyName.trim() && !this.companyNickname.trim()) { return; }
    this.state = 'saving';
    this.cliApi.postClientById(this.buildNewClient()).subscribe({
      next: (createdClient) => {
        this.createdCliId = createdClient.CLIID;
        if (!this.hasContactData()) {
          this.state = 'form';
          this.messageService.add({ severity: 'success', summary: 'Клієнт', detail: 'Нову організацію створено' });
          this.created.emit({ cliId: createdClient.CLIID, persId: null });
          return;
        }
        this.saveContact(createdClient.CLIID, true);
      },
      error: () => {
        this.state = 'form';
        this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Не вдалося створити організацію' });
      },
    });
  }

  private createContactOnly(): void {
    if (!this.cliId || !this.hasContactData()) { return; }
    this.saveContact(this.cliId, false);
  }

  private saveContact(persCliId: number, isNewCompany: boolean): void {
    this.state = 'saving';
    this.cliApi.postPersonById(this.buildNewPerson(persCliId)).subscribe({
      next: (createdPerson) => {
        this.state = 'form';
        this.messageService.add({ severity: 'success', summary: 'Контакт', detail: 'Контакт додано' });
        this.created.emit({ cliId: isNewCompany ? persCliId : null, persId: createdPerson.PERSID });
      },
      error: () => {
        this.state = 'contact-save-failed';
        this.messageService.add({
          severity: 'error', summary: 'Помилка',
          detail: isNewCompany
            ? 'Організацію створено, але не вдалося зберегти контакт. Спробуйте ще раз.'
            : 'Не вдалося зберегти контакт.',
        });
      },
    });
  }

  /** `CLIID: -2` is this codebase's existing new-record sentinel for `Client`
   *  (see `edit-client.component.ts`'s `createNewClient()`) -- the backend
   *  assigns the real `CLIID` on `postClientById`. `CASTCATID: 0` matches
   *  that same reference default, per the design doc's explicit instruction
   *  not to invent a different one. */
  private buildNewClient(): Client {
    return {
      CLIID: -2,
      CLISUBSOF: null,
      CLICATEGORY: 0,
      CLIENTSNIKNAME: this.companyNickname.trim() || this.companyName.trim(),
      CLIENTSNAME: this.companyName.trim(),
      CLIENTCOUNTRY: this.country.trim() || '',
      CLIENTCITY: this.city.trim() || '',
      CLIENTADDR1: this.street.trim() || '',
      CLIENTADR2: null,
      CLIENTZIP: this.zip.trim() || '',
      CLIENTBANK: null,
      CLIENTACCOUNT: null,
      CLIENTREGCODE: this.registrationCode.trim() || null,
      CLIENTVATREGCODE: null,
      CLIENTBANKREGCODE: null,
      CLIENTBANKCODE: null,
      CLEINTBANKCITY: null,
      CLIENTBANKADDR1: null,
      CLENTBANKADDR2: null,
      CLIENTBANKZIP: null,
      CLIENTBANKPHONE: null,
      CLIENTNOTE1: null,
      CLIENTNOTE2: null,
      CLIENTVATREGNUM: null,
      CLILOGOID: null,
      CASTCATID: 0,
      CURID: null,
      CLIDOCINDEX: null,
      CLISTAMPID: null,
      CLIENTBANK2ACC: null,
    };
  }

  /** `PERSID: -2` is this codebase's existing new-record sentinel for `Person`
   *  (see `company-director-quick-set.component.ts`'s own identical comment)
   *  -- the backend assigns the real `PERSID` on `postPersonById`.
   *  `PERSRESPONSIBLE: 0` ("Нема") since this is an ordinary contact, not
   *  the director/CEO path `company-director-quick-set` owns. */
  private buildNewPerson(persCliId: number): Person {
    return {
      PERSID: -2,
      PERSCLIID: persCliId,
      PERSONDEPT: null,
      PERSONIDCODE: null,
      PERSONFNAME: this.firstName,
      PERSONMNAME: null,
      PERSONLNAME: this.lastName,
      PERSONPHONE: this.phone,
      PERSONFAX: null,
      PERSONCPHONE: null,
      PERSONPHONE1: null,
      PERSONFAX1: null,
      PERSONNOTE: null,
      PERSONNOTE1: null,
      PERSONTITLE: null,
      PERSONCOUNTRY: null,
      PERSONCITY: null,
      PERSONADDR1: null,
      PERSONADDR2: null,
      PERSONEMAIL: this.email,
      PERSONINDEX: null,
      PERSPASSWORD: null,
      PERSSESSIONID: null,
      PERSSESSTIME: null,
      PERSRESPONSIBLE: 0,
      PERSSIGNID: null,
    };
  }
}
